export interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

export interface ServerClock {
  /** Simulated server time in milliseconds. Never read this from gameplay math directly. */
  now(): number;
  /** Moves the server clock forward. Used by tests and the local "time away" preview. */
  advance(ms: number): void;
}

const CLOCK_KEY = 'threadbound.clock.v1';
/** A single step larger than this is treated as a tampered device clock. */
const MAX_STEP_MS = 48 * 60 * 60 * 1000;

interface ClockFile {
  serverMs: number;
  wallMs: number;
}

/**
 * Local stand-in for Postgres now().
 * Wall-clock deltas advance the stored server time, except negative or absurd jumps,
 * which are ignored. The offline formula never calls the device clock itself.
 * Production claims use the database clock inside claim_offline.
 */
export function createSimulatedClock(storage: StorageLike, wallNow: () => number = Date.now): ServerClock {
  function read(): ClockFile {
    const raw = storage.getItem(CLOCK_KEY);
    if (!raw) {
      const wall = wallNow();
      const file = { serverMs: wall, wallMs: wall };
      storage.setItem(CLOCK_KEY, JSON.stringify(file));
      return file;
    }
    try {
      const parsed = JSON.parse(raw) as ClockFile;
      if (typeof parsed.serverMs !== 'number' || typeof parsed.wallMs !== 'number') throw new Error('bad clock');
      return parsed;
    } catch {
      const wall = wallNow();
      const file = { serverMs: wall, wallMs: wall };
      storage.setItem(CLOCK_KEY, JSON.stringify(file));
      return file;
    }
  }

  function write(file: ClockFile): void {
    storage.setItem(CLOCK_KEY, JSON.stringify(file));
  }

  return {
    now() {
      const file = read();
      const wall = wallNow();
      let delta = wall - file.wallMs;
      if (delta < 0 || delta > MAX_STEP_MS) delta = 0;
      const next = { serverMs: file.serverMs + delta, wallMs: wall };
      write(next);
      return next.serverMs;
    },
    advance(ms: number) {
      const current = this.now();
      write({ serverMs: current + Math.max(0, ms), wallMs: wallNow() });
    },
  };
}

export function createMemoryStorage(): StorageLike {
  const map = new Map<string, string>();
  return {
    getItem: (key) => (map.has(key) ? map.get(key)! : null),
    setItem: (key, value) => {
      map.set(key, value);
    },
    removeItem: (key) => {
      map.delete(key);
    },
  };
}

export function browserStorage(): StorageLike {
  try {
    const probe = 'threadbound.probe';
    localStorage.setItem(probe, '1');
    localStorage.removeItem(probe);
    return localStorage;
  } catch {
    return createMemoryStorage();
  }
}
